import { useContext, useEffect, useMemo, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { Button, FormGroup, Input } from 'src/components/ui';
import { omit, omitBy } from 'lodash';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import { useAppliedFormatter } from '../../util/applyFormatting';
import { useJnx } from '../../util/jnx';
import useOptionsLookup from './hooks/useOptionsLookup';
import getPathFromId from '../../util/getPathFromId';
import useWrapInput from './utils/useWrapInput';
import { useDebouncedEffect } from '../../hooks/useDebounceEffect';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTheme } from '@emotion/react';
import Icon from 'src/components/Icon';
import { useConflictResolver } from '../../blc/useConflictResolver';

function ComputedField(props) {
  const {
    registry,
    schema,
    onChange,
    fieldPathId: { $id, path: _path },
    formData: formDataFieldValue,
    uiSchema: {
      'ui:classNames': classNames = '',
      'ui:placeholder': uiPlaceholder = '',
      'ui:prefix': uiPrefix = '',
      'ui:sufix': uiSuffix = '',
      'ui:tooltipText': uiTooltipText = '',
      'ui:hideLabel': uiHideLabel = false,
      'ui:staticText': uiStaticText = false,
    },
    readonly = false,
  } = props;
  const { i18n } = useLingui();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { resolve: resolveConflicts, pendingKeys: resolvingConflictKeys } = useConflictResolver();
  const formContext = registry?.formContext;

  const auth = useContext(AuthContext);
  const isNumberField = schema?.type === 'number';

  const { title, format, jsonata: jsonataComputation, updateIf: updateIfExpr, onUpdate, lookup, hidden } = schema;

  const { sideChannel, bindings: fcBindings, setFormDataValues, parentRootData, enqueueFieldChange } = formContext;

  const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}, {}];

  const { options: lookupOptions } = useOptionsLookup({
    lookup,
    rootFormData,
    auth,
    formContext,
    formObject,
  });

  const bindings = {
    ...fcBindings,
    formObject,
    formContext,
  };

  const functionBinds = useMemo(
    () => ({
      set: setFormDataValues,
      log: (...args) => console.log('jsonata console log\n', ...args),
    }),
    [setFormDataValues]
  );
  const path = useMemo(() => getPathFromId($id), [$id]);
  const valueJnx = useJnx(jsonataComputation, { functionBinds });
  const updateIfJnx = useJnx(updateIfExpr);

  const onUpdateJnx = useJnx(onUpdate, { functionBinds });

  const calculatedValue = useMemo(() => {
    if (valueJnx) {
      const context = { ...rootFormData, lookupOptions };
      return valueJnx.eval(context, path, { ...bindings, lastValue: formDataFieldValue });
    }
    return null;
  }, [valueJnx, rootFormData, lookupOptions, path, bindings, formDataFieldValue, functionBinds]);

  // ── Conflict detection ────────────────────────────────────────────────────
  // Conflicts are stored in parentRootData.conflicts keyed by the field's $id.
  // A conflict entry has { reported, calculated, resolved } where resolved=null means unresolved.
  const conflicts = parentRootData?.conflicts ?? {};
  const conflict = conflicts?.[$id] != null && conflicts[$id] ? conflicts[$id] : null;
  const hasUnresolvedConflict = conflict != null && conflict.resolved == null;
  const hasResolvedConflict = conflict != null && conflict.resolved != null;

  useEffect(() => {
    if (conflict == null) return;
    // Resolved conflict: user picked "reported" — sync form data to their choice.
    // Unresolved: hold off entirely until the user makes a decision.
    if (conflict.resolved != null) {
      if (isNumberField) {
        const asNumber = Number(conflict.resolved);
        if (!isNaN(asNumber) && formDataFieldValue !== asNumber) {
          onChange(asNumber, _path);
        }
        return;
      }
      onChange(conflict.resolved, _path);
    }
  }, [conflict, parentRootData]);

  useDebouncedEffect(
    () => {
      try {
        if (conflict != null) return;
        // No conflict — normal calculated-value sync.
        if (calculatedValue == null) return;
        if (formDataFieldValue != calculatedValue) {
          // Batched: forms with 100+ computed fields fire these in bursts (see useFieldChangeBatcher).
          if (enqueueFieldChange) enqueueFieldChange(_path, calculatedValue);
          else onChange(calculatedValue, _path);
          const context = { ...rootFormData, lookupOptions };

          if (onUpdateJnx) {
            setTimeout(() => {
              onUpdateJnx.evalAsync(context, path, { ...bindings, value: calculatedValue });
            }, 100);
          }
          return;
        }
      } catch (e) {
        console.error('Compute Field error', e);
      }
    },
    [formDataFieldValue, calculatedValue, conflict],
    250
  );

  // The server persists the choice and useConflictResolver mirrors it into form data; the effects
  // above then sync this field from the updated conflict entry, as they do for any conflict change.
  const isResolvingConflict = resolvingConflictKeys.has($id);
  const handleUseCalculated = () => resolveConflicts([$id], 'calculated');
  const handleUseReported = () => resolveConflicts([$id], 'reported');

  let displayValue = formDataFieldValue;

  displayValue = isNumberField && displayValue === 0 ? 0 : displayValue || calculatedValue;

  const text = useAppliedFormatter(displayValue, format, i18n.locale, isNumberField);

  // useWrapInput must always be called (it contains hooks), so the picker goes in as `input`.
  const showConflictPicker = !readonly && hasUnresolvedConflict;
  const showReadonlyConflictView = readonly && hasUnresolvedConflict;
  const showConflictView = showConflictPicker || showReadonlyConflictView;

  const inputElement = showConflictView ? (
    <div id={$id} className="form-control computed-field-conflict flex flex-col gap-1 h-auto py-2">
      <small className="text-muted">
        {showConflictPicker ? (
          <Trans>
            Calculation conflict: <span className="font-bold">select which value to use</span>
          </Trans>
        ) : (
          <Trans>The reported value does not match the calculated value, and the reporting institution has not yet decided what to report.</Trans>
        )}
      </small>
      {showReadonlyConflictView ? (
        <div className="flex flex-wrap gap-2 pb-1">
          <span className="flex-grow">
            <span className="computed-field-calculated-by-us text-success">
              <Trans>Calculated by us</Trans>:
            </span>{' '}
            <em>{calculatedValue ?? conflict.calculated}</em>
          </span>
          <span className="flex-grow">
            <span className="computed-field-reported-by-you text-warning">
              <Trans>Reported</Trans>:
            </span>{' '}
            <em>{conflict.reported}</em>
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 pb-1" id={$id}>
          <Button size="sm" className="flex-grow" color="success" outline disabled={isResolvingConflict} onClick={handleUseCalculated}>
            {isMobile ? (
              <Icon icon="Calculator" />
            ) : (
              <span className="computed-field-calculated-by-us">
                <Trans>Calculated by us</Trans>:
              </span>
            )}{' '}
            <em>{calculatedValue ?? conflict.calculated}</em>
          </Button>
          <Button size="sm" className="flex-grow" color="warning" outline disabled={isResolvingConflict} onClick={handleUseReported}>
            {isMobile ? (
              <Icon icon="FileArrowUp" />
            ) : (
              <span className="computed-field-reported-by-you">
                <Trans>Reported by you</Trans>:
              </span>
            )}{' '}
            <em>{conflict.reported}</em>
          </Button>
        </div>
      )}
    </div>
  ) : (
    <Input
      disabled
      type="text"
      placeholder={`${uiPlaceholder || ''}`}
      className={`custom ${isNumberField || schema?.type === 'integer' ? 'text-right' : ''} ${classNames || ''} bg-primary/15! border-info/50! text-bold italic tracking-wide`}
      value={text}
      readOnly
      id={$id}
    />
  );

  return hidden ? null : (
    <FormGroup disabled>
      {title !== ' ' && !uiHideLabel ? (
        <label className="control-label" htmlFor="root_preferredBranchId">
          {title}
        </label>
      ) : null}
      {uiStaticText ? (
        <div className={classNames || ''}>{text}</div>
      ) : (
        useWrapInput({
          input: inputElement,
          prefix: uiPrefix,
          sufix: uiSuffix,
          tooltipText: uiTooltipText,
          rootFormData,
          dataPath: path,
          fieldId: $id,
          formContext,
          formData: formDataFieldValue,
          fieldSchema: schema,
          conflictWarning: hasUnresolvedConflict || hasResolvedConflict,
          onClearConflict: !readonly && hasResolvedConflict ? handleUseCalculated : undefined,
        })
      )}
    </FormGroup>
  );
}

export default ComputedField;
